import { Card, Alert, Divider, Button, Typography, Row, Col, Space, message } from "antd";
import { Icon } from "Components";
import { MappingItem, TenantMapping } from "../Mapping";
import { useState } from "react";
import { confirmMapping } from "Services/Api/PciCompliance/Mapping.Service";

const { Title, Text } = Typography;

interface MappingWizardStep4Props {
  tenantInfo: TenantMapping;
  fileName: string;
  mappings: MappingItem[];
  onConfirm: (count: number) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  onBack: () => void;
}

export function MappingWizardStep4({
  tenantInfo,
  fileName,
  mappings,
  onConfirm,
  onBack,
  setIsSubmitting,
}: MappingWizardStep4Props) {
  
  const validMappings = mappings.filter(
    m => !!m.encytroOffice && !!m.venzaProperty && m.status !== "removed"
  );
  const removedCount = mappings.filter(m => m.status === "removed").length;
const handleConfirm = async () => {
  setIsSubmitting(true);
  try {
    const result = await confirmMapping(
      tenantInfo,
      [...mappings.filter(m => m.status !== "removed").map((c) => ({
        property_id: c?.venzaPropertyId ?? "",
        property_name: c.venzaProperty ?? "",
        mid: c.encytroMid,
      }))],
    );

    message.success("Mapping changes confirmed successfully!");
    console.log("API response:", result);
    onConfirm(mappings.filter(m => m.status !== "removed").length);

  } catch (error: any) {
    console.error("Error:", error);

    if (error.status === 404) {
      const errorMessage =
        error.data?.errors?.[0] ||
        error.data?.message ||
        "Validation failed";
      message.error(errorMessage);
    } else if (error.status === 422) {
      const errorMessage =
        error.data?.errors?.[0] ||
        error.data?.message ||
        "Validation failed";

      message.error(errorMessage);
    } else {
      message.error("Something went wrong. Please try again.");
    }
  } finally {
    setIsSubmitting(false);
  }
};



  return (
    <Card className="mvh-80">
      <div className="mbx-16">
        <Title level={3} className="headerTitle">Confirm and Save</Title>
        <p className="headerSubtitle">Review the summary and confirm to save the hierarchy mapping</p>
      </div>
      <Alert
        type="success"
        showIcon
        className="alert-success mbx-24"
        icon={<Icon type="alertSuccess" />}
        message="All mappings have been successfully configured and are ready to save."

      />

      {/* Mapping Summary */}
      <div className="mbx-16">
        <Title level={4} className="mbx-12 fw-400 fs-1 card-foreground">Mapping Summary</Title>
        <Space direction="vertical" className="w-100" size="middle">
          <Card className="mappingSummary">
            <Space>
              <Icon type="mappingSummary" />
              <div>
                <Text type="secondary" className="text-muted-foreground">VENZA Tenant</Text>
                <div className="fs-1 card-foreground">{tenantInfo.name}</div>
              </div>
            </Space>
          </Card>
          <Card className="mappingSummary">
            <Space>
              <Icon type="tenantFile" />
              <div>
                <Text type="secondary" className="text-muted-foreground">Source File</Text>
                <div className="fs-1 card-foreground" >{fileName}</div>
              </div>
            </Space>
          </Card>
        </Space>
      </div>

      <Divider />

      <p className="sectionTitle">Mapping Statistics</p>

      <div className="grid-button text-center">
        <Card className="statusCard totalMappings" bodyStyle={{ padding: 16 }}>
          <div className="cardHeader justify-center">
            <p>Total Mappings</p>
          </div>
          <p className="cardValue">{validMappings.length}</p>
        </Card>

        <Card className="statusCard successfulMappings" bodyStyle={{ padding: 16 }}>
          <div className="cardHeader justify-center">
            <p>Successful</p>
          </div>
          <p className="alertSuccess">{validMappings.length}</p>
        </Card>

        <Card className="statusCard ignoredMappingsCard" hoverable bodyStyle={{ padding: 16 }}
        >
          <div className="cardHeader justify-center">
            <p>Removed</p>
          </div>
          <p className="cardValue">{removedCount}</p>
        </Card>
      </div>

      <Divider />

      {/* Mapped Properties */}
      <div className="mbx-24">
        <p className="sectionTitle">Mapped Properties ({validMappings.length})</p>
        <div className="mappedPropertiesList">
          {validMappings.map((mapping) => (
            <Row key={mapping.id} className="mappedPropertyRow" justify="space-between" align="middle">
              <Col xs={24} sm={12}>
                <Text className="text-muted-foreground">VENZA</Text>
                <p className="card-foreground fs-1 mb-0">{mapping?.venzaProperty}</p>
              </Col>
              <Col xs={24} sm={12}>
                <Text className="text-muted-foreground">PCI Portal</Text>
                <p className="card-foreground fs-1 mb-0">{mapping?.encytroOffice}</p>
              </Col>
            </Row>
          ))}
        </div>
      </div>

      {/* Buttons */}
      <div className="footerButtons">
        <Button className="backButton" onClick={onBack}>Back to Review</Button>
        <Button className="continueButton"  type="primary" disabled={mappings.length === 0} onClick={handleConfirm} >
          <Icon type="confirmation" className="mrx-8" />
          <span>Confirm and Save Mapping</span>
        </Button>
      </div>
    </Card>
  );

}
